import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiCalendar,
  FiEdit2,
  FiPlus,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

const colors = [
  {
    iconBg: "bg-primary-100",
    iconText: "text-primary-800",
    progress: "bg-primary-800",
  },
  {
    iconBg: "bg-green-100",
    iconText: "text-green-600",
    progress: "bg-green-600",
  },
  {
    iconBg: "bg-amber-100",
    iconText: "text-amber-600",
    progress: "bg-amber-500",
  },
  {
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    progress: "bg-blue-600",
  },
];

function currentYear() {
  return new Date().getFullYear();
}

export default function LeaveBalance() {
  const { accessToken } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [hrmsFilters, setHrmsFilters] = useState({});
  const filteredEmployees = useMemo(() => employees.filter(row => matchesHrmsFilters(row,hrmsFilters)), [employees,hrmsFilters]);
  useEffect(() => { if (!filteredEmployees.some(row => row.id === selectedEmployeeId)) setSelectedEmployeeId(filteredEmployees[0]?.id || ''); }, [filteredEmployees,selectedEmployeeId]);
  const [year, setYear] = useState(currentYear());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [balanceModal, setBalanceModal] = useState(null);
  const [recordLeaveOpen, setRecordLeaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadInitialData = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const [employeeResponse, typesResponse] = await Promise.all([
        apiRequest("/leave/employees", { accessToken }),
        apiRequest("/leave/types?activeOnly=true", { accessToken }),
      ]);

      const rawEmployees = Array.isArray(employeeResponse)
        ? employeeResponse
        : employeeResponse?.employees || employeeResponse?.data || [];

      const employeeList = rawEmployees
        .map((employee) => ({
          ...employee,
          id: employee.id || employee.employeeId,
          fullName: employee.fullName,
          employeeCode: employee.employeeCode,
          designation:
            employee.designation ||
            employee.assignments?.[0]?.designation?.name ||
            employee.assignments?.[0]?.department?.name ||
            "",
        }))
        .filter((employee) => employee.id);

      setEmployees(employeeList);
      setLeaveTypes(typesResponse || []);

      setSelectedEmployeeId((currentId) => {
        const exists = employeeList.some(
          (employee) => employee.id === currentId,
        );

        return exists ? currentId : employeeList[0]?.id || "";
      });
    } catch (requestError) {
      setError(
        requestError.message || "Unable to load leave balance data.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const loadBalances = useCallback(async () => {
    if (!accessToken || !selectedEmployeeId || !year) {
      setBalances([]);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(
        `/leave/balances?employeeId=${encodeURIComponent(
          selectedEmployeeId,
        )}&year=${year}`,
        { accessToken },
      );

      setBalances(response || []);
    } catch (requestError) {
      setBalances([]);
      setError(
        requestError.message || "Unable to load employee leave balances.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedEmployeeId, year]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const balanceCards = useMemo(() => {
    const balancesByLeaveType = new Map(
      balances.map((balance) => [balance.leaveTypeId, balance]),
    );

    return leaveTypes.map((leaveType, index) => {
      const balance = balancesByLeaveType.get(leaveType.id);
      const allocated = Number(balance?.allocatedDays || 0);
      const used = Number(balance?.usedDays || 0);
      const remaining = Math.max(allocated - used, 0);

      return {
        leaveType,
        balance,
        allocated,
        used,
        remaining,
        color: colors[index % colors.length],
      };
    });
  }, [balances, leaveTypes]);

  const totalAllocated = balanceCards.reduce(
    (sum, item) => sum + item.allocated,
    0,
  );

  const totalRemaining = balanceCards.reduce(
    (sum, item) => sum + item.remaining,
    0,
  );

  const selectedEmployee = employees.find(
    (employee) => employee.id === selectedEmployeeId,
  );

  const saveAllocation = async (allocatedDays) => {
    if (!balanceModal || !selectedEmployeeId) return;

    setSaving(true);
    setError("");

    try {
      await apiRequest("/leave/balances", {
        method: "PUT",
        accessToken,
        body: {
          employeeId: selectedEmployeeId,
          leaveTypeId: balanceModal.leaveType.id,
          year: Number(year),
          allocatedDays: Number(allocatedDays),
        },
      });

      setBalanceModal(null);
      await loadBalances();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to update leave allocation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const recordApprovedLeave = async (form) => {
    if (!selectedEmployeeId) return;

    setSaving(true);
    setError("");

    try {
      await apiRequest("/leave/records", {
        method: "POST",
        accessToken,
        body: {
          employeeId: selectedEmployeeId,
          leaveTypeId: form.leaveTypeId,
          startDate: form.startDate,
          endDate: form.endDate,
          dayUnit: form.dayUnit,
          reason: form.reason,
        },
      });

      setRecordLeaveOpen(false);
      await loadBalances();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to record approved leave.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Leave Balance
          </h1>

          <p className="mt-2 text-gray-500">
            Allocate leave, track usage, and record HR-approved leave.
          </p>
        </div>

        <button
          disabled={!selectedEmployeeId}
          onClick={() => setRecordLeaveOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-800 text-white font-medium hover:bg-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiPlus size={18} />
          Record Approved Leave
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <HrmsFilters records={employees} value={hrmsFilters} onChange={setHrmsFilters} />
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Employee
            </label>

            <select
              value={selectedEmployeeId}
              onChange={(event) =>
                setSelectedEmployeeId(event.target.value)
              }
              className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-800"
            >
              {!employees.length ? (
                <option value="">No employees found</option>
              ) : null}

              {filteredEmployees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName} · {employee.employeeCode}
                </option>
              ))}
            </select>

            {selectedEmployee?.designation ? (
              <p className="mt-2 text-xs text-gray-500">
                {selectedEmployee.designation}
              </p>
            ) : null}
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Leave Year
            </label>

            <input
              type="number"
              min="2000"
              value={year}
              onChange={(event) => setYear(event.target.value)}
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-800"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="min-h-80 flex items-center justify-center bg-white rounded-3xl border border-gray-100">
          <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
        </div>
      ) : (
        <>
          <div className="bg-gradient-to-r from-primary-800 to-primary-900 rounded-3xl shadow-lg p-8 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <p className="text-primary-100 text-sm uppercase tracking-wide">
                  Total Leave Remaining
                </p>

                <h2 className="mt-3 text-5xl font-bold">
                  {totalRemaining}
                </h2>

                <p className="mt-3 text-primary-100">
                  out of {totalAllocated} allocated leave day(s) for {year}
                </p>
              </div>

              <div className="h-20 w-20 rounded-3xl bg-white/20 flex items-center justify-center">
                <FiTrendingUp size={36} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {balanceCards.map((item) => {
              const utilization =
                item.allocated > 0
                  ? Math.min(
                      (item.used / item.allocated) * 100,
                      100,
                    )
                  : 0;

              return (
                <div
                  key={item.leaveType.id}
                  className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div
                      className={`h-14 w-14 rounded-2xl flex items-center justify-center ${item.color.iconBg} ${item.color.iconText}`}
                    >
                      <FiCalendar size={24} />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        {item.remaining} Left
                      </span>

                      <button
                        onClick={() => setBalanceModal(item)}
                        className="h-9 w-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center"
                        title="Allocate or update balance"
                      >
                        <FiEdit2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      {item.leaveType.name}
                    </h3>

                    <p className="mt-1 text-sm text-gray-500">
                      {item.leaveType.requiresBalance
                        ? "Annual leave allocation"
                        : "No leave balance required"}
                    </p>
                  </div>

                  <div className="mt-8 grid grid-cols-3 gap-4">
                    <BalanceValue label="Total" value={item.allocated} />
                    <BalanceValue
                      label="Used"
                      value={item.used}
                      className="text-primary-800"
                    />
                    <BalanceValue
                      label="Remaining"
                      value={item.remaining}
                      className="text-green-600"
                    />
                  </div>

                  <div className="mt-8">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-600">
                        Leave Utilization
                      </span>

                      <span className="text-sm font-semibold text-gray-900">
                        {Math.round(utilization)}%
                      </span>
                    </div>

                    <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${item.color.progress}`}
                        style={{ width: `${utilization}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <BalanceAllocationModal
        open={Boolean(balanceModal)}
        item={balanceModal}
        year={year}
        saving={saving}
        onClose={() => !saving && setBalanceModal(null)}
        onSave={saveAllocation}
      />

      <RecordApprovedLeaveModal
        open={recordLeaveOpen}
        leaveTypes={leaveTypes}
        employee={selectedEmployee}
        saving={saving}
        onClose={() => !saving && setRecordLeaveOpen(false)}
        onSave={recordApprovedLeave}
      />
    </div>
  );
}

function BalanceValue({ label, value, className = "text-gray-900" }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-400">
        {label}
      </p>

      <p className={`mt-2 text-2xl font-bold ${className}`}>
        {value}
      </p>
    </div>
  );
}

function BalanceAllocationModal({
  open,
  item,
  year,
  saving,
  onClose,
  onSave,
}) {
  const [allocatedDays, setAllocatedDays] = useState("");

  useEffect(() => {
    if (open) {
      setAllocatedDays(String(item?.allocated || 0));
    }
  }, [open, item]);

  if (!open || !item) return null;

  return (
    <ModalShell
      title={`Allocate ${item.leaveType.name}`}
      subtitle={`Set the annual allocation for ${year}. Used leave cannot be reduced.`}
      saving={saving}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSave(allocatedDays);
        }}
      >
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Allocated Leave Days *
        </label>

        <input
          type="number"
          required
          min={item.used}
          step="0.5"
          value={allocatedDays}
          onChange={(event) => setAllocatedDays(event.target.value)}
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-800"
        />

        <p className="mt-2 text-xs text-gray-500">
          Already used: {item.used} day(s). Allocation cannot be lower than this.
        </p>

        <ModalActions
          saving={saving}
          submitLabel="Save Allocation"
          onClose={onClose}
        />
      </form>
    </ModalShell>
  );
}

function RecordApprovedLeaveModal({
  open,
  leaveTypes,
  employee,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    dayUnit: "FULL_DAY",
    reason: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        leaveTypeId: leaveTypes[0]?.id || "",
        startDate: "",
        endDate: "",
        dayUnit: "FULL_DAY",
        reason: "",
      });
    }
  }, [open, leaveTypes]);

  if (!open) return null;

  const update = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  return (
    <ModalShell
      title="Record Approved Leave"
      subtitle={
        employee
          ? `This will directly create approved leave for ${employee.fullName}.`
          : "Select an employee first."
      }
      saving={saving}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
        className="space-y-5"
      >
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Leave Type *
          </label>

          <select
            required
            value={form.leaveTypeId}
            onChange={(event) =>
              update("leaveTypeId", event.target.value)
            }
            className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-800"
          >
            {leaveTypes.map((leaveType) => (
              <option key={leaveType.id} value={leaveType.id}>
                {leaveType.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Start Date *
            </label>

            <input
              required
              type="date"
              value={form.startDate}
              onChange={(event) =>
                update("startDate", event.target.value)
              }
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-800"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              End Date *
            </label>

            <input
              required
              type="date"
              min={form.startDate || undefined}
              value={form.endDate}
              onChange={(event) =>
                update("endDate", event.target.value)
              }
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-800"
            />
          </div>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Leave Duration *
          </label>

          <select
            value={form.dayUnit}
            onChange={(event) =>
              update("dayUnit", event.target.value)
            }
            className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-800"
          >
            <option value="FULL_DAY">Full Day</option>
            <option value="HALF_DAY">Half Day</option>
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            HR Reason / Reference *
          </label>

          <textarea
            required
            rows="3"
            maxLength={1000}
            value={form.reason}
            onChange={(event) => update("reason", event.target.value)}
            placeholder="Example: Approved paper leave application received by HR."
            className="w-full px-4 py-3 rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary-800"
          />
        </div>

        <ModalActions
          saving={saving}
          submitLabel="Record Approved Leave"
          onClose={onClose}
        />
      </form>
    </ModalShell>
  );
}

function ModalShell({
  title,
  subtitle,
  saving,
  onClose,
  children,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({
  saving,
  submitLabel,
  onClose,
}) {
  return (
    <div className="mt-7 flex justify-end gap-3">
      <button
        type="button"
        disabled={saving}
        onClick={onClose}
        className="h-11 px-6 rounded-xl border border-gray-200 disabled:opacity-50"
      >
        Cancel
      </button>

      <button
        type="submit"
        disabled={saving}
        className="h-11 px-6 rounded-xl bg-primary-800 text-white font-medium hover:bg-primary-900 disabled:opacity-50"
      >
        {saving ? "Saving..." : submitLabel}
      </button>
    </div>
  );
}
